import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card'
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  Inbox,
  PenLine,
  Settings2,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react'
import { useState } from 'react'
import {
  AppWorkspaceContentPanel,
  WorkspaceMetricStrip,
  WorkspaceNotice,
  WorkspaceSectionCard,
} from '@/components/app/workspace-shell'
import { CalendarConnectionCard } from '@/components/calendar/calendar-connection-card'
import { CalendarSourceList } from '@/components/calendar/calendar-source-list'
import {
  formatCalendarDateTime,
  formatCalendarProvider,
  getCalendarStateLabel,
} from '@/lib/calendar/calendar-status'
import type { ImportedCalendarEvent } from '@/lib/calendar/types'
import { trpc } from '@/lib/trpc'

export function CalendarIntegrationWorkspace() {
  const [showCalendarManagement, setShowCalendarManagement] = useState(false)
  const { data: connections = [] } = trpc.calendar.connections.list.useQuery()
  const { data: importedEvents = [] } = trpc.calendar.events.list.useQuery({})
  const configuredConnection = connections.find(
    (connection) => connection.status !== 'disconnected'
  )
  const configuredSources = configuredConnection?.sources ?? []
  const enabledSourceCount = configuredSources.filter((source) => source.importEnabled).length
  const writeDestination = configuredSources.find((source) => source.writeDestination)
  const needsDetailsEvents = (importedEvents as ImportedCalendarEvent[])
    .filter((event) => !event.appointmentId && event.managementState === 'needs_details')
    .slice(0, 8)

  if (!configuredConnection) {
    return (
      <AppWorkspaceContentPanel
        eyebrow="Integrations"
        description="Calendar sync is required before public launch; private testing can continue without it."
      >
        <WorkspaceNotice tone="neutral" className="mb-4 p-4">
          Connect Google Calendar or Outlook before going live so external busy events can block
          public booking slots. You can keep the booking page private while setup is still in
          progress.
        </WorkspaceNotice>
        <div className="grid gap-3 md:grid-cols-2">
          <CalendarConnectionCard provider="google" />
          <CalendarConnectionCard provider="microsoft" />
        </div>
      </AppWorkspaceContentPanel>
    )
  }

  const hasSyncIssue =
    configuredConnection.status === 'needs_reauth' || Boolean(configuredConnection.syncError)

  return (
    <AppWorkspaceContentPanel
      eyebrow="Integrations"
      description="Calendar sync blocks public booking conflicts and keeps launch readiness current."
      contentClassName="space-y-4"
    >
      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.72fr)]">
        <CalendarConnectionCard
          provider={configuredConnection.provider === 'microsoft' ? 'microsoft' : 'google'}
          connection={configuredConnection}
        />

        <WorkspaceSectionCard className="p-0">
          <CardHeader className="flex flex-row items-start justify-between gap-3 p-4">
            <div className="min-w-0">
              <CardTitle className="text-base">Calendar</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {formatCalendarProvider(
                  configuredConnection.provider === 'microsoft' ? 'microsoft' : 'google'
                )}{' '}
                connected.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setShowCalendarManagement((isOpen) => !isOpen)}
            >
              <Settings2 className="size-3.5" />
              {showCalendarManagement ? 'Hide calendars' : 'Manage calendars'}
            </Button>
          </CardHeader>
          <CardContent className="px-4 pt-0 pb-4">
            <WorkspaceMetricStrip
              metrics={[
                {
                  icon: <ShieldCheck className="size-4" />,
                  label: 'Importing',
                  value: `${enabledSourceCount} calendars`,
                },
                {
                  icon: <PenLine className="size-4" />,
                  label: 'Writing to',
                  value: writeDestination?.name ?? 'Not selected',
                },
                {
                  icon: <Clock3 className="size-4" />,
                  label: 'Last sync',
                  value: configuredConnection.lastSyncAt
                    ? formatCalendarDateTime(configuredConnection.lastSyncAt)
                    : 'Not synced yet',
                },
                {
                  icon: <Inbox className="size-4" />,
                  label: 'Needs details',
                  value: String(needsDetailsEvents.length),
                },
              ]}
            />
          </CardContent>
        </WorkspaceSectionCard>
      </section>

      {hasSyncIssue ? (
        <WorkspaceNotice tone="danger" className="flex gap-3 p-4">
          <ShieldAlert className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-1">
            <p className="font-semibold">Calendar sync needs attention</p>
            <p>
              Zippity is still using the last imported events to block booking slots, but new
              provider changes may not import until this is fixed.
            </p>
            {configuredConnection.syncError ? (
              <p className="text-xs text-destructive/80">{configuredConnection.syncError}</p>
            ) : null}
          </div>
        </WorkspaceNotice>
      ) : null}

      {showCalendarManagement ? <CalendarSourceList connections={[configuredConnection]} /> : null}

      {needsDetailsEvents.length > 0 ? (
        <WorkspaceSectionCard className="p-0">
          <CardHeader className="p-4">
            <CardTitle className="text-base">Imported events needing details</CardTitle>
            <p className="text-sm text-muted-foreground">
              Finish these from the Appointments route when they need service, customer, or location
              data.
            </p>
          </CardHeader>
          <CardContent className="px-4 pt-0 pb-4">
            <div className="flex flex-col gap-2">
              {needsDetailsEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-[6px] border border-border/70 bg-card/40 px-3 py-2.5"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="grid size-8 shrink-0 place-items-center rounded-[5px] border border-border/70 bg-background">
                      <CalendarDays className="size-3.5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {event.title}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {formatCalendarDateTime(event.startsAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {event.busy ? (
                      <Badge variant="secondary" className="gap-1 rounded-[4px]">
                        <CheckCircle2 className="size-3" />
                        Blocks booking
                      </Badge>
                    ) : null}
                    <Badge variant="outline">{getCalendarStateLabel(event)}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </WorkspaceSectionCard>
      ) : null}
    </AppWorkspaceContentPanel>
  )
}
